import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Zap, Star, Clock, Shield, ArrowRight, Gift, Sparkles, Trophy, TrendingUp, AlertCircle, Users, Lock } from 'lucide-react';

const UpgradeProfessional: React.FC = () => {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes countdown
  const [showUrgencyBanner, setShowUrgencyBanner] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 180 && !showUrgencyBanner) { // Show urgency at 3 minutes
          setShowUrgencyBanner(true);
        }
        return prevTime > 0 ? prevTime - 1 : 0;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showUrgencyBanner]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const CTASection = ({ 
    className = "", 
    buttonText = "YES! Upgrade Me To PROFESSIONAL For Just $127 →",
    subText = "One-Time Payment • Save $437/Year • 30-Day Guarantee",
    size = "large"
  }) => (
    <div className={`text-center ${className}`}>
      <button
        onClick={() => navigate('/checkout/professional-upgrade')}
        className={`group relative inline-flex items-center justify-center ${
          size === "large" ? "px-12 py-6 text-xl" : "px-8 py-4 text-lg"
        } font-bold text-white bg-gradient-to-r from-purple-500 via-blue-500 to-indigo-500 rounded-2xl shadow-2xl hover:shadow-3xl transform transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1 active:scale-[0.98]`}
      >
        <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
        <span className="relative flex items-center gap-3">
          {buttonText}
          <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
        </span>
      </button>
      <p className="mt-4 text-sm text-gray-600 flex items-center justify-center gap-4">
        <span className="flex items-center gap-1">
          <Shield className="w-4 h-4" />
          {subText}
        </span>
      </p>
    </div>
  );

  const ComparisonTable = () => (
    <div className="max-w-6xl mx-auto">
      <div className="grid md:grid-cols-2 gap-8 mb-12 relative">
        {/* Upgrade Arrow - Hidden on mobile */}
        <div className="hidden md:flex absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-full p-5 shadow-2xl">
            <ArrowRight className="w-10 h-10 text-white" />
          </div>
        </div>
        
        {/* Founder's Column */}
        <div className="relative">
          <div className="bg-gradient-to-br from-gray-700 to-gray-900 rounded-3xl p-8 text-white shadow-2xl transform hover:scale-[1.01] transition-transform duration-300">
            <div className="absolute -top-3 left-6 bg-gray-600 text-white px-4 py-1 rounded-full text-xs font-medium">
              YOUR CURRENT PLAN
            </div>
            
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold mb-2">Founder's Account</h3>
              <p className="text-gray-300 text-sm">A great start for creators</p>
            </div>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-3 bg-white/10 rounded-lg p-3">
                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span className="font-medium">AI-Powered Mockup Engine</span>
              </div>
              <div className="flex items-center gap-3 bg-white/10 rounded-lg p-3">
                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span className="font-medium">100+ Quality Templates</span>
              </div>
              <div className="flex items-center gap-3 bg-white/10 rounded-lg p-3">
                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span className="font-medium">AI Background Removal</span>
              </div>
              <div className="flex items-center gap-3 bg-white/10 rounded-lg p-3">
                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span className="font-medium">4K Resolution Exports</span>
              </div>
              <div className="flex items-center gap-3 bg-white/10 rounded-lg p-3">
                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span className="font-medium">275 Generation Credits</span>
              </div>
            </div>
            
            <div className="mt-8 pt-8 border-t border-white/20">
              <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 rounded-2xl p-6 border border-red-500/20">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center">
                    <span className="text-red-400 text-lg">✕</span>
                  </div>
                  <p className="font-bold text-white">You're Missing Out On:</p>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 bg-black/20 rounded-lg p-3">
                    <span className="text-red-400 mt-0.5">⚠️</span>
                    <div>
                      <p className="font-medium text-white">50+ Premium Templates</p>
                      <p className="text-xs text-gray-400 mt-0.5">Exclusive designs not available to Founders</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-black/20 rounded-lg p-3">
                    <span className="text-red-400 mt-0.5">⚠️</span>
                    <div>
                      <p className="font-medium text-white">725 Bonus Credits</p>
                      <p className="text-xs text-gray-400 mt-0.5">3X more creative power instantly</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-black/20 rounded-lg p-3">
                    <span className="text-red-400 mt-0.5">⚠️</span>
                    <div>
                      <p className="font-medium text-white">Brand Kits & AI Tools</p>
                      <p className="text-xs text-gray-400 mt-0.5">Professional branding & copywriting</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-black/20 rounded-lg p-3">
                    <span className="text-red-400 mt-0.5">⚠️</span>
                    <div>
                      <p className="font-medium text-white">Advanced Editor & More</p>
                      <p className="text-xs text-gray-400 mt-0.5">5+ additional pro features</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Professional Column */}
        <div className="relative">
          <div className="bg-gradient-to-br from-purple-600 via-purple-700 to-blue-700 rounded-3xl p-8 text-white shadow-2xl transform hover:scale-[1.01] transition-transform duration-300 ring-4 ring-purple-500 ring-opacity-30">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-indigo-400 to-purple-400 text-white px-6 py-2 rounded-full text-sm font-black shadow-xl flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              <span>LIMITED TIME UPGRADE</span>
              <Sparkles className="w-4 h-4" />
            </div>
            
            <div className="text-center mb-8 mt-4">
              <h3 className="text-3xl font-bold mb-2">PROFESSIONAL Account</h3>
              <p className="text-purple-200 text-sm">The complete creative powerhouse</p>
            </div>
            
            <div className="space-y-4 mb-8">
              <div className="bg-gradient-to-r from-green-500/20 to-green-600/20 rounded-lg p-3 border border-green-400/30">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="font-bold">Everything in Founder's PLUS:</span>
                </div>
              </div>
              
              <div className="space-y-4">
                {[
                  { title: "50+ Additional Pro Templates", desc: "Monthly exclusive drops" },
                  { title: "20 New Templates Monthly", desc: "Fresh designs every month" },
                  { title: "AI Text Engine", desc: "Smart text placement" },
                  { title: "Multi-Image Layering", desc: "Complex compositions" },
                  { title: "Advanced Scene Creator", desc: "Custom environments" },
                  { title: "Priority Rendering Queue", desc: "5X faster processing" },
                  { title: "725 Bonus Credits", desc: "1,000 total credits" },
                  { title: "5 Professional Brand Kits", desc: "Multi-brand management" },
                  { title: "Style Modifiers Control", desc: "Artistic variations" },
                  { title: "AI-Powered Copywriter", desc: "Headlines & CTAs" },
                  { title: "Integrated Mockup Editor", desc: "All-in-one design suite" }
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white/10 rounded-lg p-3 hover:bg-white/15 transition-colors">
                    <Star className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="font-medium">{feature.title}</span>
                      <p className="text-xs text-purple-200 mt-0.5">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-purple-800 to-blue-800 rounded-2xl p-6 text-center">
              <p className="text-xs uppercase tracking-wider text-purple-300 mb-2">Total Annual Value</p>
              <p className="text-3xl font-bold line-through opacity-60 mb-1">$564/year</p>
              <p className="text-sm text-purple-300 mb-4">Save 77% Today Only</p>
              
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-xl font-bold mb-1">Your Upgrade Price</p>
                <p className="text-5xl font-black text-indigo-400">$127</p>
                <p className="text-sm text-purple-200 mt-2">One-Time Payment • No Monthly Fees</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Google Fonts */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&family=Kalam:wght@400;700&display=swap" rel="stylesheet" />
      
      <style>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slide {
          from { transform: translateX(0); }
          to { transform: translateX(20px); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(-2deg); }
          50% { transform: translateY(-10px) rotate(2deg); }
        }
        
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.8s ease-out;
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animation-delay-500 {
          animation-delay: 0.5s;
        }
        
        .animation-delay-1000 {
          animation-delay: 1s;
        }
      `}</style>
      
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white overflow-hidden relative">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.02] pointer-events-none"></div>
      
      {/* Urgency Banner - Shows when time is low */}
      {showUrgencyBanner && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-4 shadow-lg animate-pulse">
          <div className="max-w-6xl mx-auto flex items-center justify-center gap-4 text-sm md:text-base font-bold">
            <AlertCircle className="w-5 h-5" />
            <span>⚠️ FINAL WARNING: Professional upgrade expires in {formatTime(timeLeft)} - Last chance!</span>
            <AlertCircle className="w-5 h-5" />
          </div>
        </div>
      )}

      {/* Scarcity Bar - Dark Mode Design */}
      <div className="relative bg-gradient-to-r from-gray-900 via-black to-gray-900 text-white py-4 px-4 overflow-hidden shadow-2xl border-b border-gray-700">
        {/* Animated background effect */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-blue-600/30 to-purple-600/20"></div>
          <div 
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 10px,
                rgba(255, 255, 255, 0.03) 10px,
                rgba(255, 255, 255, 0.03) 20px
              )`,
              animation: 'slide 20s linear infinite'
            }}
          ></div>
        </div>
        
        {/* Enhanced flashing lights effect */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
          <div className="absolute top-2 left-10 w-3 h-3 bg-purple-400 rounded-full animate-ping shadow-lg"></div>
          <div className="absolute top-3 right-20 w-2 h-2 bg-blue-400 rounded-full animate-ping animation-delay-500 shadow-lg"></div>
          <div className="absolute bottom-2 left-1/3 w-2 h-2 bg-indigo-400 rounded-full animate-ping animation-delay-1000 shadow-lg"></div>
          <div className="absolute top-1/2 right-1/4 w-2 h-2 bg-purple-500 rounded-full animate-pulse animation-delay-1500"></div>
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Professional Users Counter */}
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg blur opacity-75 animate-pulse"></div>
                <div className="relative flex items-center gap-2 bg-gradient-to-r from-purple-600 to-purple-700 px-5 py-2.5 rounded-lg border border-indigo-400/50 shadow-lg">
                  <div className="flex items-center gap-1">
                    <TrendingUp className="w-5 h-5 text-indigo-300 animate-bounce" />
                    <Users className="w-4 h-4 text-indigo-300 animate-pulse" />
                  </div>
                  <span className="font-black text-lg text-white drop-shadow-lg">
                    2,847+ PROFESSIONALS UPGRADED
                  </span>
                </div>
              </div>
              <div className="hidden md:flex items-center gap-2 text-sm text-indigo-200 font-medium">
                <Sparkles className="w-4 h-4" />
                <span>join the elite</span>
              </div>
            </div>

            {/* Progress Visualization */}
            <div className="flex-1 max-w-sm">
              <div className="relative">
                <div className="bg-black/50 rounded-full h-4 overflow-hidden shadow-inner">
                  <div 
                    className="relative h-full transition-all duration-500 ease-out rounded-full"
                    style={{ 
                      width: `77%`,
                      background: `linear-gradient(to right, #10b981 0%, #8b5cf6 50%, #3b82f6 100%)`
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-transparent to-white/30"></div>
                    <div className="absolute right-0 top-0 bottom-0 w-2 bg-white/50 animate-pulse"></div>
                  </div>
                </div>
                <p className="text-xs text-indigo-200 mt-1 text-center font-medium">
                  🔥 77% savings - limited time only!
                </p>
              </div>
            </div>

            {/* Timer - Enhanced */}
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg blur opacity-75 animate-pulse"></div>
              <div className="relative flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-blue-600 px-5 py-2.5 rounded-lg border border-indigo-300/50 shadow-lg">
                <Clock className="w-5 h-5 text-white animate-spin-slow drop-shadow" />
                <div>
                  <p className="text-xs text-indigo-100 font-medium">OFFER EXPIRES</p>
                  <span className="font-black text-xl text-white tabular-nums drop-shadow-lg">
                    {formatTime(timeLeft)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Hero Section - Enhanced with Gradient Background */}
      <div className="bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-600 text-white relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-blue-400 rounded-full filter blur-2xl"></div>
        </div>
        
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)`,
            }}
          ></div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-4 h-4 bg-indigo-300 rounded-full animate-float opacity-60"></div>
        <div className="absolute top-32 right-20 w-6 h-6 bg-purple-300 rounded-full animate-float animation-delay-500 opacity-40"></div>
        <div className="absolute bottom-32 left-20 w-3 h-3 bg-blue-300 rounded-full animate-float animation-delay-1000 opacity-50"></div>
        <div className="absolute top-1/3 right-10 w-5 h-5 bg-indigo-400 rounded-full animate-pulse opacity-30"></div>
        
        <div className="max-w-6xl mx-auto px-4 py-16 relative z-10">
          <div className="text-center mb-12">
            {/* Enhanced badge */}
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-indigo-400/20 to-purple-400/20 backdrop-blur-md px-8 py-4 rounded-full mb-8 animate-bounce border border-indigo-300/30 mx-auto">
              <Zap className="w-6 h-6 text-indigo-200" />
              <span className="font-bold text-lg uppercase tracking-wide text-white">WAIT! Your Order is Secure, But Not Yet Complete...</span>
              <Zap className="w-6 h-6 text-indigo-200" />
            </div>
            
            {/* Enhanced headline with handwritten font accents */}
            <div className="space-y-6">
              <h1 className="text-6xl md:text-8xl font-black mb-6 leading-tight text-center">
                <span 
                  className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-300 drop-shadow-2xl animate-float"
                  style={{ fontFamily: "'Caveat', cursive", fontWeight: 700 }}
                >
                  WAIT!
                </span>
              </h1>
              
              <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Read This 
                <span className="relative inline-block mx-4">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-orange-300 to-yellow-300 underline decoration-wavy decoration-4 underline-offset-8">
                    Private Page
                  </span>
                  <div className="absolute -top-2 -right-2">
                    <Sparkles className="w-8 h-8 text-yellow-300 animate-pulse" />
                  </div>
                </span>
                Carefully
              </h2>
              
              <p className="text-2xl md:text-3xl text-purple-100 font-medium">
                Before Proceeding to the Members Area
              </p>
              
              {/* Decorative line */}
              <div className="flex items-center justify-center mt-8">
                <div className="h-1 w-32 bg-gradient-to-r from-transparent via-indigo-300 to-transparent rounded-full"></div>
                <Star className="w-6 h-6 text-indigo-300 mx-4 animate-spin-slow" />
                <div className="h-1 w-32 bg-gradient-to-r from-transparent via-indigo-300 to-transparent rounded-full"></div>
              </div>
            </div>
          </div>

          {/* Video Placeholder - Now inside hero */}
          <div className="mb-8 animate-fadeIn max-w-4xl mx-auto">
            <div 
              className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl aspect-video flex items-center justify-center shadow-2xl relative overflow-hidden cursor-pointer hover:shadow-3xl transition-all duration-300 group border border-white/20"
            >
              {/* Animated background pattern */}
              <div className="absolute inset-0 opacity-30">
                <div 
                  className="absolute inset-0 animate-pulse"
                  style={{
                    backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255, 255, 255, 0.03) 35px, rgba(255, 255, 255, 0.03) 70px)`,
                  }}
                ></div>
              </div>
              
              {/* Play button */}
              <div className="text-center z-10 group-hover:scale-110 transition-transform duration-300">
                <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center mb-6 mx-auto shadow-2xl group-hover:shadow-3xl transition-shadow">
                  <div className="w-0 h-0 border-l-[40px] border-l-purple-600 border-t-[24px] border-t-transparent border-b-[24px] border-b-transparent ml-2"></div>
                </div>
                <p className="text-white font-bold text-3xl mb-3 drop-shadow-lg">Unlock Professional Features</p>
                <p className="text-indigo-200 text-xl drop-shadow">Watch this exclusive upgrade message</p>
              </div>

              {/* Video duration badge */}
              <div className="absolute bottom-6 right-6 bg-black/70 backdrop-blur text-white px-5 py-3 rounded-xl font-bold text-lg shadow-lg">
                3:47
              </div>
              
              {/* Corner decorations */}
              <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-white/30 rounded-tl-lg"></div>
              <div className="absolute top-4 right-4 w-8 h-8 border-r-2 border-t-2 border-white/30 rounded-tr-lg"></div>
              <div className="absolute bottom-4 left-4 w-8 h-8 border-l-2 border-b-2 border-white/30 rounded-bl-lg"></div>
              <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-white/30 rounded-br-lg"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* Congratulations Section */}
        <div 
          className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-3xl p-8 mb-12 max-w-4xl mx-auto shadow-lg animate-fadeIn animate-delay-300"
        >
          <div className="flex items-start gap-4">
            <Trophy className="w-12 h-12 text-green-600 flex-shrink-0 animate-bounce" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Congratulations! Your Founder's Access to BrandScapes AI has been successfully created.
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You've just secured a powerful tool that will save you countless hours and hundreds of dollars on design costs. 
                In just a few moments, you'll be redirected to your dashboard to start creating.
              </p>
              <p className="text-gray-700 leading-relaxed font-medium">
                You now hold the key to creating stunning, professional mockups faster than ever before. 
                You've made a smart investment in making your brand look more professional and trustworthy.
              </p>
            </div>
          </div>
        </div>

        {/* The Hook */}
        <div className="max-w-4xl mx-auto mb-12">
          <p className="text-xl text-gray-700 leading-relaxed mb-6">
            But as you begin to use the platform, you'll quickly discover there's a difference between having a powerful tool... 
            <span className="font-bold text-gray-900">and commanding a complete creative arsenal.</span>
          </p>
          <p className="text-xl text-gray-700 leading-relaxed mb-6">
            The Founder's version is fantastic for generating high-quality visuals. But it was designed with certain limitations. 
            What happens when you need visuals that are truly unique, with custom text, advanced scene arrangements, 
            and an ever-expanding library of templates to ensure you always stand out from the crowd?
          </p>
          {/* The Problem Card - Modern Professional Design */}
          <div className="relative mb-12 max-w-3xl mx-auto">
            {/* Main Card */}
            <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
              {/* Top Accent Bar */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-purple-500 via-blue-500 to-indigo-500"></div>
              
              {/* Content */}
              <div className="p-10">
                {/* Quote Mark */}
                <div className="absolute top-8 left-8 text-8xl text-purple-200 font-serif leading-none">"</div>
                
                {/* Main Text */}
                <div className="relative z-10 space-y-6">
                  <p className="text-xl text-gray-700 leading-relaxed font-medium">
                    You've made a smart investment in the Founder's tools, but there's a difference between having good tools...
                  </p>
                  
                  {/* Highlighted Problem */}
                  <div className="text-center py-8">
                    <div className="inline-block relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-blue-400 transform rotate-1 rounded-lg"></div>
                      <div className="relative bg-white px-8 py-4 rounded-lg shadow-lg transform -rotate-1 transition-transform hover:rotate-0">
                        <p className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                          And being a true professional
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Explanation */}
                  <div className="space-y-4">
                    <p className="text-gray-600 leading-relaxed text-lg">
                      The Founder's version gives you the basics. But what happens when you need advanced features, premium templates, and unlimited creative power?
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-8">
                      <div className="text-center p-4 bg-purple-50 rounded-xl">
                        <Lock className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                        <p className="text-sm font-semibold text-gray-700">Limited features</p>
                        <p className="text-xs text-gray-500">basic functionality only</p>
                      </div>
                      <div className="text-center p-4 bg-blue-50 rounded-xl">
                        <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                        <p className="text-sm font-semibold text-gray-700">Fewer templates</p>
                        <p className="text-xs text-gray-500">standard designs</p>
                      </div>
                      <div className="text-center p-4 bg-indigo-50 rounded-xl">
                        <Zap className="w-8 h-8 text-indigo-500 mx-auto mb-2" />
                        <p className="text-sm font-semibold text-gray-700">Slower processing</p>
                        <p className="text-xs text-gray-500">standard queue</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Bottom Question */}
                <div className="mt-10 pt-8 border-t border-gray-200">
                  <p className="text-center text-2xl font-bold text-gray-800">
                    What if you could unlock 
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600"> the complete professional suite?</span>
                  </p>
                </div>
              </div>
              
              {/* Bottom Gradient Fade */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 via-blue-500 to-indigo-500"></div>
            </div>
            
            {/* Shadow Effect */}
            <div className="absolute -bottom-4 left-8 right-8 h-20 bg-gradient-to-b from-gray-200/50 to-transparent rounded-3xl blur-xl"></div>
          </div>

          {/* Premium Opportunity Section */}
          <div className="relative overflow-hidden">
            {/* Premium Card */}
            <div 
              className="bg-gradient-to-br from-purple-900 via-purple-800 to-blue-900 rounded-3xl p-10 text-center shadow-2xl animate-fadeIn relative"
            >
              {/* Animated Background Pattern */}
              <div className="absolute inset-0 opacity-10 pointer-events-none">
                <div 
                  className="absolute inset-0 animate-pulse"
                  style={{
                    backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255, 255, 255, 0.1) 35px, rgba(255, 255, 255, 0.1) 70px)`,
                  }}
                ></div>
              </div>
              
              {/* Top Badge */}
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                <div className="bg-gradient-to-r from-indigo-400 to-purple-400 text-white px-6 py-1 rounded-full text-xs font-black shadow-lg flex items-center gap-2">
                  <Sparkles className="w-3 h-3" />
                  EXCLUSIVE PROFESSIONAL INVITATION
                  <Sparkles className="w-3 h-3" />
                </div>
              </div>
              
              {/* Timer Section */}
              <div className="relative mb-8 mt-6">
                <div className="bg-black/30 backdrop-blur rounded-2xl p-4 max-w-sm mx-auto border border-white/20">
                  <p className="text-indigo-400 text-sm font-bold mb-2 uppercase tracking-wider">⏰ Professional Offer Expires:</p>
                  <div className="text-5xl font-black text-white tabular-nums">
                    {formatTime(timeLeft)}
                  </div>
                  <p className="text-purple-300 text-xs mt-2">Limited time professional upgrade</p>
                </div>
              </div>
              
              {/* Icon and Main Content */}
              <div className="relative">
                <div className="bg-gradient-to-br from-purple-500 to-blue-500 w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center shadow-2xl">
                  <Trophy className="w-10 h-10 text-white" />
                </div>
                
                <h3 className="text-3xl font-black text-white mb-4 leading-tight">
                  Upgrade to Professional Status
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
                    And Unlock Everything
                  </span>
                </h3>
                
                <p className="text-lg text-purple-100 mb-6 leading-relaxed max-w-2xl mx-auto">
                  For a limited time, on this page only, we're offering our new founding members 
                  the chance to upgrade to Professional status with massive savings.
                </p>
                
                {/* Value Highlight */}
                <div className="bg-white/10 backdrop-blur rounded-2xl p-4 max-w-md mx-auto border border-white/20">
                  <p className="text-sm text-purple-300 mb-1">Normally $564/year • Today just</p>
                  <p className="text-4xl font-black text-indigo-400">$127</p>
                  <p className="text-xs text-purple-300 mt-1">77% OFF • One-Time Payment</p>
                </div>
              </div>
              
              {/* Urgency Text */}
              <p className="text-sm text-indigo-300 mt-6 font-medium">
                ⚠️ When this timer hits zero, this offer disappears forever
              </p>
            </div>
          </div>
        </div>

        {/* Main Offer */}
        <div 
          className="text-center mb-12 animate-fadeInScale"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Unlock the Full, Unrestricted Power of BrandScapes AI...
            <br />
            <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              By Upgrading to the PROFESSIONAL Version.
          </span>
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-8">
            This is your <span className="font-bold text-red-600">one and only opportunity</span> to add our complete suite of advanced features, 
            which will be sold as a recurring monthly subscription after this launch, 
            to your account for a single, one-time payment.
        </p>
      </div>

        {/* Visual Comparison Note */}
        <div className="text-center mb-8">
          <div className="inline-flex flex-col items-center gap-2">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg">
              <Sparkles className="w-5 h-5" />
              SIDE-BY-SIDE COMPARISON
              <Sparkles className="w-5 h-5" />
            </div>
            <p className="text-gray-700 text-lg mt-2 font-medium">Your current access vs. <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600 font-bold">Everything you could have</span></p>
          </div>
        </div>

        {/* Comparison Table */}
        <ComparisonTable />

        {/* First CTA */}
        <CTASection className="mb-16" />

        {/* Enhanced Pro Features Section */}
        <div className="mb-16 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Here's Exactly What You're Unlocking When You
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                Upgrade to Professional Today...
              </span>
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto">
              You currently have access to a fantastic set of tools. But the <span className="font-bold text-purple-600">ten professional-grade features</span> below 
              are what separate the amateurs from the pros, giving you an unfair advantage in your market.
            </p>
          </div>

          {/* Features Overview Grid */}
          <div className="grid md:grid-cols-5 gap-4 mb-12 max-w-5xl mx-auto">
            {[
              { icon: "📚", title: "50+ Pro Templates" },
              { icon: "✍️", title: "AI Text Engine" },
              { icon: "🎨", title: "Multi-Layering" },
              { icon: "🎬", title: "Scene Creator" },
              { icon: "⚡", title: "Priority Queue" },
              { icon: "💳", title: "725 Credits" },
              { icon: "🏢", title: "5 Brand Kits" },
              { icon: "🎭", title: "Style Modifiers" },
              { icon: "📝", title: "AI Copywriter" },
              { icon: "🖼️", title: "Mockup Editor" }
            ].map((feature, i) => (
              <div key={i} className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-shadow text-center transform hover:-translate-y-1 transition-transform">
                <div className="text-3xl mb-2">{feature.icon}</div>
                <p className="text-sm font-semibold text-gray-800">{feature.title}</p>
              </div>
            ))}
              </div>
              
          {/* PRO FEATURE #1 */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-3xl p-8 md:p-12 shadow-xl">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <span className="text-white font-black text-2xl">1</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    PRO FEATURE #1: Expand Your Library with the Exclusive "Pro" Template Club
                  </h3>
                  <p className="text-gray-700 text-lg mb-6">
                    The 100+ templates in your Founder's account are a great start, but what happens when you've used the best ones? 
                    Your marketing starts to look repetitive.
                  </p>
                  
                  <div className="bg-white rounded-2xl p-8 shadow-md mb-6">
                    <p className="text-gray-700 text-lg mb-4">
                      With the Professional upgrade, you immediately unlock our private collection of <span className="font-bold text-purple-600">50+ additional, 
                      premium templates</span>. These are our most sophisticated scenes, featuring exclusive products and environments 
                      not available to standard users.
                    </p>
                    <p className="text-gray-700 text-lg font-semibold">But it gets better.</p>
                  </div>
                  
                  <p className="text-gray-700 text-lg mb-6">
                    To ensure your visuals always stay fresh, you will also receive <span className="font-bold text-blue-600">20 brand-new, 
                    professionally art-directed templates added to your account every single month</span> for the next 12 months. 
                    That's 240+ extra templates over the next year! This feature alone guarantees your marketing will never look stale.
                  </p>
                  
                  {/* Visual Element - Template Carousel */}
                  <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-2xl p-6 text-center">
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mb-4">
                      {[...Array(12)].map((_, i) => (
                        <div key={i} className="bg-white rounded-lg h-20 shadow-md animate-fadeInScale" style={{animationDelay: `${i * 50}ms`}}></div>
                      ))}
                    </div>
                    <p className="text-sm text-gray-600 italic">Premium templates showcase - Actual templates 10x more stunning!</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* PRO FEATURE #2 */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-3xl p-8 md:p-12 shadow-xl">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <span className="text-white font-black text-2xl">2</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    PRO FEATURE #2: Turn Mockups into Ads with the AI Text Engine
                  </h3>
                  <p className="text-gray-700 text-lg mb-6">
                    A great mockup is a perfect starting point, but it's not a finished ad. Until now, you'd have to export 
                    your mockup and use another program to add your headline or call to action.
                  </p>
                  
                  <div className="bg-white rounded-2xl p-8 shadow-md mb-6">
                    <p className="text-gray-700 text-lg mb-4">
                      <span className="font-bold text-blue-600">With the Professional AI Text Engine, that extra step is eliminated.</span>
                    </p>
                    <p className="text-gray-700 text-lg">
                      You can now add, edit, and style text directly onto any mockup. Our AI intelligently analyzes the scene 
                      to wrap your text around objects, match the perspective, and adopt the correct lighting and shadows. 
                      Add a killer headline to a billboard mockup or a "50% Off" slogan to a t-shirt. This turns BrandScapes AI 
                      from a mockup tool into a complete graphical ad creator.
                    </p>
                  </div>
                  
                  {/* Visual Element - Text Engine Demo */}
                  <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-2xl p-8 text-center">
                    <div className="bg-white rounded-xl p-6 shadow-inner max-w-md mx-auto">
                      <div className="h-32 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg mb-4 relative overflow-hidden">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <p className="text-2xl font-bold text-gray-700 transform -rotate-12 animate-fadeInScale">
                            World's Best Coffee
                          </p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 italic">AI Text Engine wraps text perfectly around any object</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* PRO FEATURE #3 */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-3xl p-8 md:p-12 shadow-xl">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-gradient-to-br from-green-600 to-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <span className="text-white font-black text-2xl">3</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    PRO FEATURE #3: Create Complex Designs with the Multi-Image Layering System
                  </h3>
                  <p className="text-gray-700 text-lg mb-6">
                    Your current version lets you place one design into a scene. 
                    <span className="font-bold"> The Professional version lets you place unlimited designs in a single scene.</span>
                  </p>
                  
                  <div className="bg-white rounded-2xl p-8 shadow-md mb-6">
                    <p className="text-gray-700 text-lg">
                      Want to put a large graphic on the back of a hoodie, a small logo on the chest, and your website URL 
                      down the sleeve? Now you can, all in one project. Our intuitive layering system lets you independently 
                      control the size and position of each image, giving you the power to create hyper-realistic and detailed 
                      mockups that truly represent your vision.
                    </p>
                  </div>
                  
                  {/* Visual Element - Layering Demo */}
                  <div className="bg-gradient-to-r from-green-100 to-blue-100 rounded-2xl p-8">
                    <div className="relative max-w-md mx-auto">
                      <div className="bg-gray-300 rounded-lg h-48 relative shadow-lg">
                        <div className="absolute top-4 left-4 w-16 h-16 bg-blue-500 rounded-lg shadow-md animate-fadeInLeft animate-delay-100"></div>
                        <div className="absolute bottom-4 right-4 w-24 h-24 bg-purple-500 rounded-lg shadow-md animate-fadeInLeft animate-delay-200"></div>
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-8 bg-green-500 rounded shadow-md animate-fadeInLeft animate-delay-300"></div>
                      </div>
                      <p className="text-sm text-gray-600 italic mt-4 text-center">Exploded view: Multiple designs → One stunning mockup</p>
                    </div>
                  </div>
                </div>
                </div>
              </div>
            </div>

          {/* PRO FEATURE #4 */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-3xl p-8 md:p-12 shadow-xl">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-600 to-red-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <span className="text-white font-black text-2xl">4</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    PRO FEATURE #4: Build Your Own Worlds with the Advanced Scene Creator
                  </h3>
                  <p className="text-gray-700 text-lg mb-6">
                    Why be limited by our templates when you can create your own? 
                    <span className="font-bold"> The Advanced Scene Creator gives you the ultimate creative freedom.</span>
                  </p>
                  
                  <div className="bg-white rounded-2xl p-8 shadow-md mb-6">
                    <p className="text-gray-700 text-lg">
                      Instantly change the background of any scene, upload your own custom environments, and arrange products 
                      within the mockup. You get granular control over shadow intensity, direction, and environmental reflections. 
                      This allows you to create visuals that perfectly match your website's design, your brand's color palette, 
                      or the specific mood of a marketing campaign. Your creative possibilities are now infinite.
                    </p>
                  </div>
                  
                  {/* Visual Element - Scene Creator Demo */}
                  <div className="bg-gradient-to-r from-orange-100 to-red-100 rounded-2xl p-8">
                    <div className="bg-white rounded-xl p-6 shadow-inner max-w-md mx-auto">
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-gray-600">Background:</span>
                          <div className="h-8 bg-gradient-to-r from-blue-300 to-purple-300 rounded flex-1"></div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-gray-600">Shadow:</span>
                          <input type="range" className="flex-1" defaultValue="75" />
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-gray-600">Reflection:</span>
                          <input type="range" className="flex-1" defaultValue="50" />
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 italic mt-4 text-center">Full control over every visual element</p>
                    </div>
                </div>
                </div>
              </div>
                </div>
              </div>
              
          {/* PRO FEATURE #5 */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-3xl p-8 md:p-12 shadow-xl">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-600 to-orange-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <span className="text-white font-black text-2xl">5</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    PRO FEATURE #5: Jump to the Front of the Line with Priority Rendering
                  </h3>
                  <p className="text-gray-700 text-lg mb-6">
                    As a Professional member, your account is flagged for VIP treatment. Your mockup requests are automatically 
                    pushed to the front of our processing queue. During peak times and major product launches when our servers 
                    are busiest, you'll experience significantly faster rendering speeds, allowing you to get your campaign 
                    visuals ready while others are still waiting.
                  </p>
                  
                  {/* Visual Element - Priority Queue */}
                  <div className="bg-gradient-to-r from-yellow-100 to-orange-100 rounded-2xl p-8">
                    <div className="max-w-md mx-auto">
                      <div className="space-y-4">
                        <div className="bg-gray-200 rounded-lg p-4 relative overflow-hidden">
                          <p className="text-sm font-medium text-gray-600">Standard Queue</p>
                          <div className="flex gap-2 mt-2">
                            {[...Array(8)].map((_, i) => (
                              <div key={i} className="w-8 h-8 bg-gray-400 rounded"></div>
                            ))}
                          </div>
                        </div>
                        <div className="bg-gradient-to-r from-yellow-300 to-orange-300 rounded-lg p-4 relative overflow-hidden shadow-lg">
                          <p className="text-sm font-bold text-gray-800">VIP Priority Lane</p>
                          <div className="flex gap-2 mt-2">
                            <div className="w-8 h-8 bg-yellow-600 rounded shadow-md flex items-center justify-center">
                              <Star className="w-4 h-4 text-white" />
                            </div>
                            <div className="w-8 h-8 bg-gray-400 rounded"></div>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 italic mt-4 text-center">Skip the wait - Get results 5x faster</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* PRO FEATURE #6 */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-3xl p-8 md:p-12 shadow-xl">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-gradient-to-br from-pink-600 to-purple-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <span className="text-white font-black text-2xl">6</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    PRO FEATURE #6: Instantly 3X Your Creative Power with a Bonus Bank of 725 Pro Credits
                  </h3>
                  <p className="text-gray-700 text-lg mb-6">
                    Your new Founder's account has been pre-loaded with 275 credits, giving you a fantastic start to generate 
                    dozens of professional mockups for your most important projects.
                  </p>
                  
                  <div className="bg-white rounded-2xl p-8 shadow-md mb-6">
                    <p className="text-gray-700 text-lg mb-4">
                      But as you begin creating visuals for every ad campaign, social media post, client request, and product 
                      variation, you'll quickly realize that for a serious marketer, credits are like rocket fuel—the more you have, 
                      the faster and further you can go.
                    </p>
                    <p className="text-gray-700 text-lg font-semibold">This is why the Professional upgrade includes a massive credit injection.</p>
                  </div>
                  
                  <p className="text-gray-700 text-lg mb-6">
                    When you upgrade now, we will immediately deposit a <span className="font-bold text-pink-600">bonus bank of 725 additional credits</span> into 
                    your account. This brings your total available balance to a massive 1,000 credits. This huge increase gives you 
                    the ultimate freedom to experiment, create for multiple brands or clients, A/B test hundreds of visual concepts, 
                    and use our most advanced AI tools without ever worrying about running low.
                  </p>
                  
                  {/* Visual Element - Credit Card */}
                  <div className="bg-gradient-to-r from-pink-100 to-purple-100 rounded-2xl p-8 text-center">
                    <div className="bg-gradient-to-br from-pink-600 to-purple-600 rounded-2xl p-8 max-w-sm mx-auto shadow-2xl transform hover:scale-105 transition-transform">
                      <div className="text-white">
                        <p className="text-sm font-medium mb-2 opacity-90">BONUS CREDITS</p>
                        <p className="text-5xl font-black mb-2">725</p>
                        <div className="w-full h-0.5 bg-white/30 my-4"></div>
                        <p className="text-lg font-semibold">Total Balance: 1,000 Credits</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 italic mt-4">3X more creative power at your fingertips</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* PRO FEATURE #7 */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-3xl p-8 md:p-12 shadow-xl">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-600 to-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <span className="text-white font-black text-2xl">7</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    PRO FEATURE #7: Unlock 5 Professional Brand Kits
                  </h3>
                  <p className="text-gray-700 text-lg mb-6">
                    Stop wasting time searching for your brand assets every time you create a mockup. 
                    <span className="font-bold"> With the Professional upgrade, you unlock Brand Kits.</span>
                  </p>
                  
                  <div className="bg-white rounded-2xl p-8 shadow-md mb-6">
                    <p className="text-gray-700 text-lg">
                      A Brand Kit is your centralized hub for each brand you manage. Upload your logos, save your exact brand 
                      color palettes, and define your fonts just once. Create up to 5 distinct Brand Kits—perfect for agencies 
                      managing multiple clients, marketers with different product lines, or e-commerce sellers with several stores. 
                      Now, you can apply perfect, consistent branding to any mockup in a single click.
                    </p>
                  </div>
                  
                  {/* Visual Element - Brand Kits UI */}
                  <div className="bg-gradient-to-r from-indigo-100 to-blue-100 rounded-2xl p-6">
                    <div className="bg-white rounded-xl p-4 shadow-inner">
                      <p className="text-sm font-semibold text-gray-700 mb-4">Your Brand Kits</p>
                      <div className="space-y-3">
                        {[
                          { name: "Main Brand", colors: ["#6366f1", "#8b5cf6", "#ec4899"] },
                          { name: "Client A", colors: ["#3b82f6", "#06b6d4", "#10b981"] },
                          { name: "Client B", colors: ["#f59e0b", "#ef4444", "#f97316"] },
                          { name: "Product Line 1", colors: ["#8b5cf6", "#d946ef", "#f43f5e"] },
                          { name: "Product Line 2", colors: ["#14b8a6", "#22c55e", "#84cc16"] }
                        ].map((kit, i) => (
                          <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <div className="w-10 h-10 bg-gray-300 rounded flex items-center justify-center text-xs font-bold text-gray-600">
                              LOGO
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-800">{kit.name}</p>
                            </div>
                            <div className="flex gap-1">
                              {kit.colors.map((color, j) => (
                                <div key={j} className="w-6 h-6 rounded" style={{backgroundColor: color}}></div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 italic mt-4 text-center">Manage up to 5 brand identities seamlessly</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* PRO FEATURE #8 */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-teal-50 to-green-50 rounded-3xl p-8 md:p-12 shadow-xl">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-600 to-green-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <span className="text-white font-black text-2xl">8</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    PRO FEATURE #8: Gain Full Control with Style Modifiers
                  </h3>
                  <p className="text-gray-700 text-lg mb-6">
                    Go beyond the standard template look and become the art director. 
                    <span className="font-bold"> Style Modifiers give you advanced control to guide the AI's creative output.</span>
                  </p>
                  
                  <div className="bg-white rounded-2xl p-8 shadow-md mb-6">
                    <p className="text-gray-700 text-lg">
                      Add powerful keywords like "cinematic lighting," "minimalist aesthetic," "vintage photo," or "vibrant and bold" 
                      to influence the mood, lighting, and texture of your final mockup. This powerful feature allows you to generate 
                      truly unique visuals that perfectly match the specific style of your campaign, ensuring your images never look 
                      like anyone else's.
                    </p>
              </div>

                  {/* Visual Element - Style Variations */}
                  <div className="bg-gradient-to-r from-teal-100 to-green-100 rounded-2xl p-6">
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="bg-white rounded-xl p-4 shadow-md">
                        <div className="h-32 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg mb-3 flex items-center justify-center">
                          <p className="text-white font-bold text-sm">PRODUCT</p>
                        </div>
                        <p className="text-sm font-semibold text-gray-800">Cinematic</p>
                        <p className="text-xs text-gray-600">Dramatic shadows, film noir</p>
                      </div>
                      <div className="bg-white rounded-xl p-4 shadow-md">
                        <div className="h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg mb-3 flex items-center justify-center">
                          <p className="text-gray-800 font-bold text-sm">PRODUCT</p>
                        </div>
                        <p className="text-sm font-semibold text-gray-800">Minimalist</p>
                        <p className="text-xs text-gray-600">Clean, simple, modern</p>
                      </div>
                      <div className="bg-white rounded-xl p-4 shadow-md">
                        <div className="h-32 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-lg mb-3 flex items-center justify-center relative overflow-hidden">
                          <div className="absolute inset-0 bg-gradient-to-br from-transparent to-brown-100 opacity-50"></div>
                          <p className="text-orange-900 font-bold text-sm relative">PRODUCT</p>
                        </div>
                        <p className="text-sm font-semibold text-gray-800">Vintage</p>
                        <p className="text-xs text-gray-600">Retro, faded, nostalgic</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 italic mt-4 text-center">Same product, infinite artistic styles</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* PRO FEATURE #9 */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-3xl p-8 md:p-12 shadow-xl">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-pink-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <span className="text-white font-black text-2xl">9</span>
        </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    PRO FEATURE #9: Generate Killer Copy with the Built-in AI Copywriter
                  </h3>
                  <p className="text-gray-700 text-lg mb-6">
                    What's a great visual without a great headline? 
                    <span className="font-bold"> The Professional version includes a revolutionary AI-Powered Copywriting tool built directly into the editor.</span>
                  </p>
                  
                  <div className="bg-white rounded-2xl p-8 shadow-md mb-6">
                    <p className="text-gray-700 text-lg">
                      Struggling with what to say? Let the AI generate compelling headlines, short product descriptions, and powerful 
                      calls-to-action for your mockups. This feature, powered by your Pro Credits, streamlines your entire creative 
                      process, allowing you to generate both the stunning visual and the high-converting text for your ads in one place.
                    </p>
      </div>

                  {/* Visual Element - AI Copywriter Demo */}
                  <div className="bg-gradient-to-r from-red-100 to-pink-100 rounded-2xl p-6">
                    <div className="bg-white rounded-xl p-6 shadow-inner max-w-lg mx-auto">
                      <div className="mb-4">
                        <button className="bg-gradient-to-r from-red-600 to-pink-600 text-white px-4 py-2 rounded-lg font-semibold text-sm hover:shadow-lg transition-shadow">
                          Generate Headline
                        </button>
                      </div>
                      <div className="space-y-3">
                        <div className="p-3 bg-gray-50 rounded-lg border-2 border-transparent hover:border-pink-300 transition-colors cursor-pointer">
                          <p className="font-bold text-gray-800">"Transform Your Style Today"</p>
                          <p className="text-xs text-gray-600 mt-1">Confidence: 95%</p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg border-2 border-transparent hover:border-pink-300 transition-colors cursor-pointer">
                          <p className="font-bold text-gray-800">"Unleash Your Creative Power"</p>
                          <p className="text-xs text-gray-600 mt-1">Confidence: 92%</p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg border-2 border-pink-300 cursor-pointer">
                          <p className="font-bold text-gray-800">"Your Design, Your Rules"</p>
                          <p className="text-xs text-gray-600 mt-1">Confidence: 89%</p>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 italic mt-4 text-center">AI-powered copy that converts, instantly</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* PRO FEATURE #10 */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-3xl p-8 md:p-12 shadow-xl">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-gradient-to-br from-gray-700 to-gray-900 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <span className="text-white font-black text-2xl">10</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    PRO FEATURE #10: Perfect Your Visuals with the Integrated Mockup Editor
                  </h3>
                  <p className="text-gray-700 text-lg mb-6">
                    Your mockup is generated, but you want to make a few final tweaks. Instead of exporting to another program, 
                    just open our new <span className="font-bold">Integrated Mockup Editor.</span>
                  </p>
                  
                  <div className="bg-white rounded-2xl p-8 shadow-md mb-6">
                    <p className="text-gray-700 text-lg">
                      This powerful, Canva-like editor is the final step to perfection. Crop and resize your image, add text overlays 
                      using the AI Copywriter, insert shapes and icons, apply filters, and even combine multiple mockups into a single 
                      graphic. The Mockup Editor turns BrandScapes AI into a true all-in-one design suite, eliminating the need for 
                      any other software.
                    </p>
                  </div>
                  
                  {/* Visual Element - Editor UI */}
                  <div className="bg-gradient-to-r from-gray-200 to-gray-300 rounded-2xl p-6">
                    <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
                      <div className="bg-gray-800 p-3 flex items-center gap-2">
                        <div className="flex gap-1.5">
                          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                          <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        </div>
                        <p className="text-xs text-gray-400 ml-2">Mockup Editor Pro</p>
                      </div>
                      <div className="flex">
                        <div className="w-16 bg-gray-100 p-4 space-y-3">
                          <div className="w-8 h-8 bg-gray-300 rounded"></div>
                          <div className="w-8 h-8 bg-gray-300 rounded"></div>
                          <div className="w-8 h-8 bg-gray-300 rounded"></div>
                          <div className="w-8 h-8 bg-gray-300 rounded"></div>
                        </div>
                        <div className="flex-1 p-8 bg-gray-50">
                          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
                            <div className="w-32 h-32 bg-gradient-to-br from-purple-400 to-blue-400 rounded-lg mx-auto mb-4"></div>
                            <p className="text-gray-700 font-semibold">Your Mockup Here</p>
                          </div>
                        </div>
                        <div className="w-48 bg-gray-100 p-4">
                          <p className="text-xs font-semibold text-gray-700 mb-3">Properties</p>
                          <div className="space-y-2">
                            <div className="bg-white rounded p-2">
                              <p className="text-xs text-gray-600">Text</p>
                            </div>
                            <div className="bg-white rounded p-2">
                              <p className="text-xs text-gray-600">Shapes</p>
                            </div>
                            <div className="bg-white rounded p-2">
                              <p className="text-xs text-gray-600">Filters</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 italic mt-4 text-center">Professional editing suite built right in</p>
              </div>
            </div>
              </div>
            </div>
          </div>

          {/* Total Value Summary */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl p-8 text-white text-center shadow-2xl">
            <h3 className="text-3xl font-bold mb-6">
              Lock In Your Professional Access Forever
            </h3>
            <div className="max-w-3xl mx-auto">
              <div className="bg-white/10 backdrop-blur rounded-2xl p-8 mb-6">
                <h4 className="text-2xl font-bold mb-4">All 10 Professional Features Included:</h4>
                <div className="grid md:grid-cols-2 gap-3 text-left">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-yellow-400" />
                    <span>50+ Premium Templates</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-yellow-400" />
                    <span>20 New Templates Monthly</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-yellow-400" />
                    <span>AI Text Engine</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-yellow-400" />
                    <span>Multi-Image Layering</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-yellow-400" />
                    <span>Advanced Scene Creator</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-yellow-400" />
                    <span>Priority Rendering Queue</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-yellow-400" />
                    <span>725 Bonus Credits</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-yellow-400" />
                    <span>5 Professional Brand Kits</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-yellow-400" />
                    <span>Style Modifiers Control</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-yellow-400" />
                    <span>Integrated Mockup Editor</span>
                  </div>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-xl mb-2 opacity-90">Professional Access Normally Costs:</p>
                <p className="text-5xl font-bold line-through opacity-60 mb-4">$564/year</p>
                <p className="text-xl mb-6 opacity-90">Today Only:</p>
                <div className="bg-gradient-to-r from-yellow-400 to-orange-400 text-gray-900 rounded-2xl p-6">
                  <p className="text-6xl font-black mb-2">$127</p>
                  <p className="text-xl font-bold">One-Time Payment</p>
                  <p className="text-lg">Never Pay Again • 77% OFF</p>
                </div>
              </div>
              
              <p className="text-lg font-medium">
                ⚡ This special pricing expires when the timer hits zero
              </p>
            </div>
          </div>
        </div>

        {/* CTA After Pro Features */}
        <CTASection className="mb-16" />

        {/* Social Proof Section */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <Users className="w-12 h-12 text-purple-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900">
              Join 2,847+ Professionals Who Already Upgraded
            </h3>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div 
              className="bg-white p-6 rounded-2xl shadow-lg animate-fadeInLeft animate-delay-100"
            >
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-700 italic mb-3">
                "The AI Text Engine alone is worth the upgrade. I can finally add custom text to my mockups without Photoshop!"
              </p>
              <p className="text-sm font-semibold text-gray-900">Sarah M.</p>
              <p className="text-xs text-gray-500">E-commerce Store Owner</p>
            </div>
            
            <div 
              className="bg-white p-6 rounded-2xl shadow-lg animate-fadeInLeft animate-delay-200"
            >
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-700 italic mb-3">
                "Priority rendering saves me hours every week. My mockups are ready in seconds instead of minutes."
              </p>
              <p className="text-sm font-semibold text-gray-900">Michael R.</p>
              <p className="text-xs text-gray-500">Digital Marketing Agency</p>
            </div>
            
            <div 
              className="bg-white p-6 rounded-2xl shadow-lg animate-fadeInLeft animate-delay-300"
            >
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-700 italic mb-3">
                "The extra templates are incredible. My designs look so much more professional now!"
              </p>
              <p className="text-sm font-semibold text-gray-900">Jessica L.</p>
              <p className="text-xs text-gray-500">Print-on-Demand Seller</p>
            </div>
          </div>
        </div>

        {/* Additional Benefits */}
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-3xl p-12 mb-16 max-w-4xl mx-auto">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-8">
            Why Upgrade Right Now?
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div 
              className="flex items-start gap-4 bg-white p-6 rounded-2xl shadow-md transition-all duration-300 hover:scale-105"
            >
              <TrendingUp className="w-8 h-8 text-purple-600 flex-shrink-0" />
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Save $47/Month Forever</h4>
                <p className="text-gray-700">After this offer expires, Professional access will cost $47/month. Lock in no monthly fees now!</p>
              </div>
            </div>
            <div 
              className="flex items-start gap-4 bg-white p-6 rounded-2xl shadow-md transition-all duration-300 hover:scale-105"
            >
              <Sparkles className="w-8 h-8 text-blue-600 flex-shrink-0" />
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Instant Access to Everything</h4>
                <p className="text-gray-700">All Pro features unlock immediately. No waiting, no restrictions.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Urgency Section */}
        <div 
          className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-3xl p-8 mb-12 max-w-4xl mx-auto animate-fadeInScale"
        >
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4 animate-pulse" />
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              This Page Will Disappear When The Timer Hits Zero
            </h3>
              <p className="text-lg text-gray-700 mb-6">
                Once you leave this page, you'll never see this offer again. 
                Professional will only be available at the regular price of $564/year.
              </p>
            <div className="text-4xl font-bold text-red-600 mb-4">
              {formatTime(timeLeft)}
            </div>
            <p className="text-sm text-gray-600">
              Don't let this opportunity slip away. Join thousands who've already upgraded.
            </p>
        </div>
      </div>

      {/* Final CTA */}
        <div className="text-center">
          <p className="text-2xl font-semibold text-gray-900 mb-8">
            This special upgrade offer is only available on this page, right now.
          </p>
          <CTASection />
        </div>

        {/* Guarantee Section */}
        <div className="mt-16 mb-12 text-center max-w-3xl mx-auto">
          <Shield className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            30-Day Money Back Guarantee
          </h3>
          <p className="text-gray-700 leading-relaxed">
            Try Professional risk-free for 30 days. If you're not completely satisfied with the advanced features, 
            we'll refund your upgrade payment - no questions asked. You can keep your Founder's access.
          </p>
        </div>

        {/* Skip Link */}
        <div className="text-center mt-12">
          <button 
            onClick={() => navigate('/dashboard')}
            className="text-gray-500 underline hover:text-gray-700 text-sm transition-colors"
          >
            No thanks, I'll stick with the limitations →
          </button>
        </div>
      </div>

      {/* FOOTER - Professional Design */}
      <footer className="relative bg-gray-900 text-gray-300 mt-20">
        {/* Top border accent */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600"></div>
      
      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* Logo and Company Info */}
        <div className="text-center mb-12">
          <img 
            src="/clawbrand_logo_color.png" 
            alt="ClawBrand AI" 
            className="h-16 mx-auto mb-6"
          />
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Transform your marketing with AI-powered mockups. Create stunning visuals in seconds, not hours.
            </p>
          </div>
          
        {/* Footer Links */}
        <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-12 text-sm">
          <a href="/support" className="hover:text-white transition-colors duration-200">Support</a>
          <span className="text-gray-600">•</span>
          <a href="/privacy" className="hover:text-white transition-colors duration-200">Privacy Policy</a>
          <span className="text-gray-600">•</span>
          <a href="/refund" className="hover:text-white transition-colors duration-200">Refund Policy</a>
          <span className="text-gray-600">•</span>
          <a href="/cookies" className="hover:text-white transition-colors duration-200">Cookie Policy</a>
          <span className="text-gray-600">•</span>
          <a href="/terms" className="hover:text-white transition-colors duration-200">Terms of Service</a>
          <span className="text-gray-600">•</span>
          <a href="/disclaimer" className="hover:text-white transition-colors duration-200">Disclaimer</a>
        </div>
        
        {/* Disclaimers */}
        <div className="space-y-6 text-xs text-gray-500 max-w-4xl mx-auto">
          {/* Facebook Disclaimer */}
          <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
            <h4 className="font-semibold text-gray-400 mb-2 uppercase tracking-wider">Facebook Disclaimer</h4>
            <p className="leading-relaxed">
              In no way are we associated with Facebook. Once you leave Facebook the responsibility is no longer on any Instagram owned entity.
              Your information and privacy is totally secure and will NEVER be shared. Facebook is in NO WAY associated with this website or anything here.
            </p>
          </div>
          
          {/* General Disclaimer */}
          <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
            <h4 className="font-semibold text-gray-400 mb-2 uppercase tracking-wider">General Disclaimer</h4>
            <p className="leading-relaxed">
              Please note that this product does not provide any guarantee of income or success. The results achieved by the product owner or any other individuals mentioned are not indicative of future success or earnings. This website is not affiliated with FaceBook or any of its associated entities. Once you navigate away from FaceBook, the responsibility for the content and its usage lies solely with the user. All content on this website, including but not limited to text, images, and multimedia, is protected by copyright law and the Digital Millennium Copyright Act. Unauthorized copying, duplication, modification, or theft, whether intentional or unintentional, is strictly prohibited. Violators will be prosecuted to the fullest extent of the law. We want to clarify that JVZoo serves as the retailer for the products featured on this site. JVZoo® is a registered trademark of BBC Systems Inc., a Florida corporation located at 1809 E. Broadway Street, Suite 125, Oviedo, FL 32765, USA, and is used with permission. The role of JVZoo as a retailer does not constitute an endorsement, approval, or review of these products or any claims, statements, or opinions used in their promotion.
            </p>
          </div>
        </div>
        
        {/* Copyright */}
          <div className="text-center mt-12 pt-8 border-t border-gray-800">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} BrandScapes AI. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default UpgradeProfessional;